/**
 * Composable canvas rendering functions for the 2D warehouse layout.
 * Each function draws a single layer, making the rendering pipeline testable and readable.
 */

import {
  RackPosition,
  RackDimensions,
  WarehouseDimensions,
  WarehouseObject,
  EditorState,
  PreviewRackPosition,
  HeatmapData,
  LocationActivity,
  ACTIVITY_COLORS,
  ActivityType,
  RubberBandState,
} from '../types/warehouseLayout';
import { getRotatedDimensions } from './positionCalculator';
import { PlacedBinRenderInfo, OBJECT_TYPE_COLORS } from './layoutApiMapping';

export interface RenderContext {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  scale: number;
  offsetX: number;
  offsetY: number;
}

export function computeRenderContext(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  dimensions: WarehouseDimensions,
  zoom: number,
  panOffset: { x: number; y: number }
): RenderContext {
  const scale = Math.min(
    (canvasWidth - 40) / dimensions.length,
    (canvasHeight - 40) / dimensions.width
  ) * zoom;

  const offsetX = (canvasWidth - dimensions.length * scale) / 2 + panOffset.x;
  const offsetY = (canvasHeight - dimensions.width * scale) / 2 + panOffset.y;

  return { ctx, width: canvasWidth, height: canvasHeight, scale, offsetX, offsetY };
}

export function drawBackground(rc: RenderContext) {
  rc.ctx.fillStyle = '#1f2937';
  rc.ctx.fillRect(0, 0, rc.width, rc.height);
}

export function drawFloor(rc: RenderContext, dimensions: WarehouseDimensions) {
  rc.ctx.fillStyle = '#374151';
  rc.ctx.fillRect(rc.offsetX, rc.offsetY, dimensions.length * rc.scale, dimensions.width * rc.scale);
}

/**
 * Plantegning-underlag: strekkes over hele gulvflaten med dempet opasitet,
 * slik at reoler kan plasseres «oppå tegningen». Tegnes etter gulv, før grid.
 */
export function drawBackgroundImage(
  rc: RenderContext,
  image: HTMLImageElement,
  dimensions: WarehouseDimensions
) {
  rc.ctx.save();
  rc.ctx.globalAlpha = 0.45;
  rc.ctx.drawImage(
    image,
    rc.offsetX,
    rc.offsetY,
    dimensions.length * rc.scale,
    dimensions.width * rc.scale
  );
  rc.ctx.restore();
}

export function drawGrid(rc: RenderContext, dimensions: WarehouseDimensions, gridUnit: number) {
  rc.ctx.strokeStyle = '#4b5563';
  rc.ctx.lineWidth = 0.5;
  rc.ctx.beginPath();

  for (let x = 0; x <= dimensions.length; x += gridUnit) {
    const screenX = rc.offsetX + x * rc.scale;
    rc.ctx.moveTo(screenX, rc.offsetY);
    rc.ctx.lineTo(screenX, rc.offsetY + dimensions.width * rc.scale);
  }

  for (let y = 0; y <= dimensions.width; y += gridUnit) {
    const screenY = rc.offsetY + y * rc.scale;
    rc.ctx.moveTo(rc.offsetX, screenY);
    rc.ctx.lineTo(rc.offsetX + dimensions.length * rc.scale, screenY);
  }

  rc.ctx.stroke();
}

export function drawDimensionLabels(rc: RenderContext, dimensions: WarehouseDimensions) {
  rc.ctx.fillStyle = '#9ca3af';
  rc.ctx.font = '12px sans-serif';
  rc.ctx.textAlign = 'center';
  rc.ctx.fillText(`${dimensions.length}m`, rc.offsetX + (dimensions.length * rc.scale) / 2, rc.offsetY - 8);
  rc.ctx.save();
  rc.ctx.translate(rc.offsetX - 8, rc.offsetY + (dimensions.width * rc.scale) / 2);
  rc.ctx.rotate(-Math.PI / 2);
  rc.ctx.fillText(`${dimensions.width}m`, 0, 0);
  rc.ctx.restore();
}

/**
 * Faste objekter (søyler, porter, stasjoner, sonemarkører) — tegnes under reolene.
 */
export function drawObjects(
  rc: RenderContext,
  objects: WarehouseObject[],
  showLabels: boolean
) {
  objects.forEach(obj => {
    const color = obj.color || OBJECT_TYPE_COLORS[obj.type] || '#6b7280';
    const rotated = (obj.rotation === 90 || obj.rotation === 270)
      ? { width: obj.depth, depth: obj.width }
      : { width: obj.width, depth: obj.depth };

    const screenX = rc.offsetX + obj.x * rc.scale;
    const screenY = rc.offsetY + obj.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    rc.ctx.fillStyle = color + '66'; // ~40 % alpha
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.strokeStyle = color;
    rc.ctx.lineWidth = 1.5;
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);

    if (showLabels && obj.name && screenWidth > 30) {
      rc.ctx.fillStyle = '#e5e7eb';
      rc.ctx.font = '10px sans-serif';
      rc.ctx.textAlign = 'center';
      rc.ctx.textBaseline = 'middle';
      rc.ctx.fillText(obj.name, screenX, screenY);
    }
  });
}

/**
 * Enkeltlokasjoner (gulv/vegg/heng/skap/dock) — tegnes over gulvet, under reolene.
 */
export function drawBinPositions(
  rc: RenderContext,
  bins: PlacedBinRenderInfo[],
  showLabels: boolean,
  selectedBinCode?: string | null
) {
  bins.forEach(bin => {
    const rotated = (bin.rotation === 90 || bin.rotation === 270)
      ? { width: bin.depthM, depth: bin.widthM }
      : { width: bin.widthM, depth: bin.depthM };

    const screenX = rc.offsetX + bin.x * rc.scale;
    const screenY = rc.offsetY + bin.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    rc.ctx.fillStyle = bin.color + '4D'; // ~30 % alpha
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.strokeStyle = bin.color;
    rc.ctx.lineWidth = 1.5;
    rc.ctx.setLineDash([4, 3]); // stiplet = enkeltlokasjon, ikke reol
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.setLineDash([]);

    // Gul markering av valgt enkeltlokasjon (samme stil som reol-seleksjon)
    if (selectedBinCode && bin.binCode === selectedBinCode) {
      rc.ctx.strokeStyle = '#fbbf24';
      rc.ctx.lineWidth = 3;
      rc.ctx.strokeRect(screenX - screenWidth / 2 - 2, screenY - screenDepth / 2 - 2, screenWidth + 4, screenDepth + 4);
    }

    if (showLabels && screenWidth > 24) {
      rc.ctx.fillStyle = '#f3f4f6';
      rc.ctx.font = 'bold 10px sans-serif';
      rc.ctx.textAlign = 'center';
      rc.ctx.textBaseline = 'middle';
      rc.ctx.fillText(bin.binCode, screenX, screenY);
    }
  });
}

export function drawPreviewRacks(
  rc: RenderContext,
  previews: PreviewRackPosition[],
  rackDimensionsMap: Map<string, RackDimensions>
) {
  previews.forEach(preview => {
    const dim = rackDimensionsMap.get(preview.rackNumber) || {
      width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5,
    };

    const rotated = getRotatedDimensions(dim, preview.rotation);
    const screenX = rc.offsetX + preview.x * rc.scale;
    const screenY = rc.offsetY + preview.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    rc.ctx.fillStyle = preview.isValid ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)';
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);

    rc.ctx.strokeStyle = preview.isValid ? '#22c55e' : '#ef4444';
    rc.ctx.lineWidth = 2;
    rc.ctx.setLineDash([5, 5]);
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.setLineDash([]);

    rc.ctx.fillStyle = preview.isValid ? '#22c55e' : '#ef4444';
    rc.ctx.font = 'bold 11px sans-serif';
    rc.ctx.textAlign = 'center';
    rc.ctx.textBaseline = 'middle';
    rc.ctx.fillText(preview.rackNumber, screenX, screenY);
  });
}

export function drawRacks(
  rc: RenderContext,
  racks: RackPosition[],
  rackDimensionsMap: Map<string, RackDimensions>,
  selectedRacks: Set<string>,
  anchorRack: string | null,
  showLabels: boolean,
  showHeatmap: boolean,
  showActivity: boolean,
  heatmapLookup: Map<string, number>,
  activityLookup: Map<string, LocationActivity[]>
) {
  racks.forEach(rack => {
    const dim = rackDimensionsMap.get(rack.rackNumber) || {
      width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5,
    };

    const rotated = getRotatedDimensions(dim, rack.rotation);
    const screenX = rc.offsetX + rack.x * rc.scale;
    const screenY = rc.offsetY + rack.y * rc.scale;
    const screenWidth = rotated.width * rc.scale;
    const screenDepth = rotated.depth * rc.scale;

    const isSelected = selectedRacks.has(rack.rackNumber);
    const isAnchor = anchorRack === rack.rackNumber;

    // Determine rack color — egendefinert farge fra egenskaps-panelet som grunnfarge
    let fillColor = rack.color || '#3b82f6';
    if (showHeatmap) {
      const intensity = heatmapLookup.get(rack.rackNumber) || 0;
      const r = Math.round(255 * intensity);
      const g = Math.round(255 * (1 - intensity) * 0.5);
      const b = Math.round(100 * (1 - intensity));
      fillColor = `rgb(${r}, ${g}, ${b})`;
    }

    const rackActivities = activityLookup.get(rack.rackNumber) || [];
    if (rackActivities.length > 0 && !showHeatmap) {
      fillColor = ACTIVITY_COLORS[rackActivities[0].activityType as ActivityType] || fillColor;
    }

    // Rack rectangle
    rc.ctx.fillStyle = fillColor;
    rc.ctx.globalAlpha = isSelected ? 1 : 0.8;
    rc.ctx.fillRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);
    rc.ctx.globalAlpha = 1;

    // Border
    rc.ctx.strokeStyle = '#1e3a5f';
    rc.ctx.lineWidth = 2;
    rc.ctx.strokeRect(screenX - screenWidth / 2, screenY - screenDepth / 2, screenWidth, screenDepth);

    // Selection border
    if (isSelected || isAnchor) {
      rc.ctx.strokeStyle = isAnchor ? '#22c55e' : '#fbbf24';
      rc.ctx.lineWidth = 3;
      rc.ctx.strokeRect(screenX - screenWidth / 2 - 2, screenY - screenDepth / 2 - 2, screenWidth + 4, screenDepth + 4);
    }

    // Label
    if (showLabels) {
      rc.ctx.fillStyle = '#ffffff';
      rc.ctx.font = 'bold 14px sans-serif';
      rc.ctx.textAlign = 'center';
      rc.ctx.textBaseline = 'middle';
      rc.ctx.fillText(rack.label || rack.rackNumber, screenX, screenY);

      rc.ctx.font = '10px sans-serif';
      rc.ctx.fillStyle = '#d1d5db';
      rc.ctx.fillText(`${dim.verticals}\u00d7${dim.shelves}`, screenX, screenY + 14);
    }

    // Activity indicator
    if (rackActivities.length > 0 && showActivity) {
      const pulseSize = 8 + Math.sin(Date.now() / 200) * 3;
      rc.ctx.beginPath();
      rc.ctx.arc(screenX + screenWidth / 2 - 10, screenY - screenDepth / 2 + 10, pulseSize, 0, Math.PI * 2);
      rc.ctx.fillStyle = ACTIVITY_COLORS[rackActivities[0].activityType as ActivityType] || '#22c55e';
      rc.ctx.fill();
    }

    // Anchor icon
    if (isAnchor) {
      rc.ctx.fillStyle = '#22c55e';
      rc.ctx.font = 'bold 16px sans-serif';
      rc.ctx.fillText('\u2693', screenX - screenWidth / 2 + 10, screenY - screenDepth / 2 + 12);
    }
  });
}

export function drawRubberBand(rc: RenderContext, rubberBand: RubberBandState) {
  if (!rubberBand.isActive) return;

  const { startX, startY, currentX, currentY } = rubberBand;
  const x = Math.min(startX, currentX);
  const y = Math.min(startY, currentY);
  const w = Math.abs(currentX - startX);
  const h = Math.abs(currentY - startY);

  rc.ctx.strokeStyle = '#3b82f6';
  rc.ctx.lineWidth = 2;
  rc.ctx.setLineDash([5, 5]);
  rc.ctx.strokeRect(x, y, w, h);
  rc.ctx.setLineDash([]);

  rc.ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
  rc.ctx.fillRect(x, y, w, h);
}

/**
 * Canvas-overlays: kun m\u00e5lestokk-indikator.
 * Utvalgsinfo vises i statuslinjen nederst og hurtigtastene i toolbarens hjelp-popover.
 */
export function drawOverlays(rc: RenderContext, scale: number) {
  // Scale indicator
  rc.ctx.fillStyle = '#9ca3af';
  rc.ctx.font = '11px sans-serif';
  rc.ctx.textAlign = 'left';
  const scaleBarLength = 5;
  const scaleBarPixels = scaleBarLength * scale;
  rc.ctx.fillRect(20, rc.height - 30, scaleBarPixels, 4);
  rc.ctx.fillText(`${scaleBarLength}m`, 20, rc.height - 38);
}
